// Consistent imports
import { type RouteRecordRaw } from 'vue-router';

import Landing from '@/views/landing/LandingContainer.vue';
import NavigationError from '@/components/common/navigation/NavigationError.vue';
import ProfileContainer from '@/components/account/ProfileContainer.vue';
import UserProfileContainer from '@/components/users/UserProfileContainer.vue';

import { adminRoute } from '@/router/administrationRoutes';
import { depotRoute } from '@/router/depotRoutes';
import { tenantRoute } from '@/router/tenantRoutes';

// Build-time behavior:
// - non-prod resolves to `designGuideRoutes.ts`
// - prod resolves to `designGuideRoutes.prod.ts` (empty array)
// See alias mapping in `vite.config.js`.
import { designGuideRoutes } from '@/router/designGuideRoutes';

import { usePermissionsStore } from '@/stores/permissions';

// Lazy-loaded components for better performance
const ChangePwd = () => import('@/views/login/ChangePwd.vue');
const ForceChangePwd = () => import('@/views/login/ForceChangePwd.vue');
const UserLogin = () => import('@/views/login/UserLogin.vue');
const ForgotPwd = () => import('@/views/login/ForgotPwd.vue');
const InsufficientPermissions = () =>
  import('@/views/InsufficientPermissions.vue');
// Helper function for context permission checks
const createContextGuard = (contextName) => {
  return async (to, from, next) => {
    const permissionsStore = usePermissionsStore();
    if (permissionsStore.hasContext(contextName)) {
      next();
    } else {
      next({ name: 'notFound' });
    }
  };
};

// Group routes by their purpose
const contextRoutes = [
  {
    beforeEnter: createContextGuard('admin'),
    ...adminRoute
  },
  {
    beforeEnter: createContextGuard('depots'),
    ...depotRoute
  },
  {
    beforeEnter: createContextGuard('tenants'),
    ...tenantRoute
  }
];

const authRoutes: RouteRecordRaw[] = [
  {
    component: ChangePwd,
    name: 'changePwd',
    path: '/changePwd',
    props: (route) => ({ ...route.params }),
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: ForceChangePwd,
    name: 'forceChangePwd',
    path: '/forceChangePwd',
    props: (route) => ({ ...route.params }),
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: UserLogin,
    name: 'login',
    path: '/login',
    meta: { unguarded: true, layout: 'public' }
  },
  {
    component: ForgotPwd,
    name: 'forgotPwd',
    path: '/forgotPwd',
    meta: { unguarded: true, layout: 'public' }
  }
];

const singleContextGuard = async (to, from, next) => {
  const permissionsStore = usePermissionsStore();

  if (permissionsStore.hasSingleContext) {
    const { contexts } = permissionsStore;
    if (contexts.length === 1) {
      return next({ name: contexts[0]?.path });
    }
  }

  next();
};

const mainRoutes: RouteRecordRaw[] = [
  {
    component: Landing,
    name: 'landing',
    path: '/landing',
    meta: {
      unguarded: false,
      fillHeight: true,
      hasBasicLayout: true
    },
    beforeEnter: singleContextGuard
  },
  {
    name: 'dc',
    path: '/dc/*',
    redirect: '/'
  },
  {
    name: 'dashboard',
    path: '/',
    redirect: '/landing',
    meta: { unguarded: true }
  }
];

const NotFoundPage = () =>
  import('@/components/errorHandling/NotFoundPage.vue');
const MainErrorComponent = () =>
  import('@/components/errorHandling/MainErrorComponent.vue');
const ErrorHandlingContainer = () =>
  import('@/components/errorHandling/ErrorHandlingContainer.vue');

const errorRoutes: RouteRecordRaw[] = [
  {
    component: InsufficientPermissions,
    name: 'noPermissions',
    path: '/no-permissions'
  },
  {
    component: NotFoundPage,
    name: 'notFound',
    path: '/not-found',
    meta: {
      hasBasicLayout: true,
      fillHeight: true
    }
  },
  {
    children: [
      {
        components: {
          default: MainErrorComponent,
          navigation: NavigationError
        },
        name: 'errorCode',
        path: ':errorCode',
        props: {
          default: true,
          navigation: true
        }
      }
    ],
    components: {
      default: ErrorHandlingContainer
    },
    name: 'error',
    path: '/error'
  }
];

const userRoutes: RouteRecordRaw[] = [
  {
    name: 'account',
    path: '/my',
    meta: {
      hasBasicLayout: true
    },
    children: [
      {
        component: ProfileContainer,
        name: 'profile',
        path: 'profile'
      }
    ]
  },
  {
    name: 'user',
    path: '/users',
    meta: {
      hasBasicLayout: true
    },
    children: [
      {
        component: UserProfileContainer,
        name: 'userProfile',
        path: 'profile/:userHashId',
        props: true
      }
    ]
  }
];

// Combine all route groups
const routes: RouteRecordRaw[] = [
  ...contextRoutes,
  ...mainRoutes,
  ...authRoutes,
  ...errorRoutes,
  ...userRoutes,
  // Always spread this import. In prod, Vite swaps it to an empty array.
  ...designGuideRoutes,
  // Catch-all 404 route - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'catchAll404',
    redirect: '/not-found'
  }
];

export default routes;
